/**
 * StartHereStrip — 3-lesson curated row that surfaces the Study area inside
 * the vault dashboard. Picks the three highest-signal lessons for a member
 * who's just landed: the Prompt Playbook (foundational), the marketer
 * bookmark set (tactical), and the hook formulas (conversion).
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { STUDY_LESSONS } from "../data/study_data";

const CURATED = [
  "prompt-playbook",
  "8-chatgpt-prompts-every-marketer-should-bookmark",
  "hook-formulas",
];

export function StartHereStrip() {
  const lessons = CURATED
    .map((slug) => STUDY_LESSONS.find((l) => l.slug === slug))
    .filter(Boolean);

  if (!lessons.length) return null;

  return (
    <section
      className="mt-14 md:mt-20 border-t border-purple-500/15 pt-10 md:pt-14"
      data-testid="start-here-strip"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6 md:mb-8">
        <div>
          <div className="text-purple-400 text-xs md:text-sm tracking-[0.35em] uppercase mb-3 font-mono inline-flex items-center gap-2">
            <BookOpen size={12} strokeWidth={2.5} />
            Start here · the study room
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
            Three lessons before your first track.
          </h2>
          <p className="mt-3 text-zinc-400 text-base md:text-lg max-w-xl">
            Short, tactical, no fluff. Read these before opening your first prompt and everything downstream compounds harder.
          </p>
        </div>
        <Link
          to="/study"
          className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 text-xs tracking-[0.25em] uppercase font-bold transition-colors"
          data-testid="start-here-all-lessons"
        >
          All lessons <ArrowUpRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900">
        {lessons.map((l) => (
          <Link
            key={l.slug}
            to={`/study/${l.slug}`}
            className="group bg-[#0a0a0a] hover:bg-[#0d0a14] p-6 md:p-7 flex flex-col transition-colors duration-300 border border-transparent hover:border-purple-500/40"
            data-testid={`start-here-${l.slug}`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-purple-300 text-[10px] tracking-[0.3em] uppercase font-mono">
                Lesson
              </span>
              <span className="text-zinc-500 text-[10px] tracking-[0.2em] uppercase font-mono">
                {l.read_minutes} min read
              </span>
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight mb-3">
              {l.title}
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed flex-1 mb-6">
              {l.subtitle}
            </p>
            <span className="text-purple-400 group-hover:text-purple-300 text-sm font-medium inline-flex items-center gap-2 transition-all duration-300 group-hover:gap-3">
              Read lesson <ArrowUpRight size={16} />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
